import { ChevronDown, Download, Loader2, Paperclip, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";

import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import type {
  BuilderLegalEntityDealEvent,
  BuilderLegalRelationshipType,
} from "@/lib/api/databook";
import { PROJECT_DOCUMENT_ACCEPT } from "@/lib/api/documents";
import { cn } from "@/lib/utils";

import { LegalEntityActivitySummary } from "./LegalEntityActivitySummary";
import { BUILDER_CURRENCIES } from "./currencies";
import {
  LegalEntityFileCoverage,
  type LegalEntityFileCoverageProps,
} from "./LegalEntityFileCoverage";
import {
  dealEventPresentation,
  isMidPeriodDealEvent,
  type LegalStructureAnalysisInterval,
} from "./legalStructure";

interface ParentOption {
  id: string;
  name: string;
}

export interface LegalEntityDraftAttachment {
  key: string;
  documentId: string | null;
  filename: string;
  status: "uploading" | "ready" | "error";
}

interface LegalEntityCardProps {
  name: string;
  businessCurrency: string;
  businessCurrencyLabel: "Operating currency" | "Presentation currency";
  sourceCurrencies?: string[];
  reportsInto?: string[];
  inPerimeter?: boolean;
  highlighted?: boolean;
  highlights?: string[];
  dealEvents?: BuilderLegalEntityDealEvent[];
  analysisInterval?: LegalStructureAnalysisInterval;
  activitySummary?: string | null;
  consolidatedGroup?: boolean;
  fileCoverage: LegalEntityFileCoverageProps;
  userAddedMessage?: string;
  draftAttachments?: LegalEntityDraftAttachment[];
  attachmentActionsDisabled?: boolean;
  editable?: boolean;
  perimeterEditable?: boolean;
  parentEntityId?: string | null;
  relationshipType?: BuilderLegalRelationshipType | null;
  parentOptions?: ParentOption[];
  onNameChange?: (value: string) => void;
  onBusinessCurrencyChange?: (value: string) => void;
  onRelationshipChange?: (
    parentEntityId: string | null,
    relationshipType: BuilderLegalRelationshipType | null,
  ) => void;
  onTogglePerimeter?: () => void;
  onActivitySummaryChange?: (value: string) => void;
  onDealEventsChange?: (value: BuilderLegalEntityDealEvent[]) => void;
  onRemove?: () => void;
  onAttachFiles?: (files: File[]) => void;
  onDetachAttachment?: (attachmentKey: string) => void;
  onDownloadAttachment?: (attachment: LegalEntityDraftAttachment) => void;
}

const RELATIONSHIP_TYPES: BuilderLegalRelationshipType[] = [
  "owns",
  "controls",
  "subsidiary",
  "consolidates",
  "includes_in_group",
  "unknown",
];
const EMPTY_DEAL_EVENTS: BuilderLegalEntityDealEvent[] = [];

export function BusinessCurrencyPill({
  value,
  label,
  sourceCurrencies,
  onChange,
}: {
  value: string;
  label: "Operating currency" | "Presentation currency";
  sourceCurrencies: string[];
  onChange?: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filteredCurrencies = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return BUILDER_CURRENCIES;
    return BUILDER_CURRENCIES.filter((currency) =>
      currency.code.toLowerCase().includes(normalizedQuery)
      || currency.label.toLowerCase().includes(normalizedQuery));
  }, [query]);
  const title = sourceCurrencies.length > 0
    ? `${label}. Source currencies: ${sourceCurrencies.join(", ")}`
    : label;

  if (!onChange) {
    return (
      <span
        className="rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-semibold text-foreground"
        title={title}
      >
        {value}
      </span>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Select ${label.toLowerCase()}`}
          title={title}
          className="inline-flex items-center gap-1 rounded-md border border-border/70 bg-foreground/[0.04] px-2 py-0.5 text-[11px] font-semibold tabular-nums text-foreground transition-colors hover:bg-foreground/10"
        >
          {value}
          <ChevronDown className="h-2.5 w-2.5 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="end">
        <div className="border-b border-border/60 p-2">
          <Input
            autoFocus
            placeholder="Search currency…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="h-8 text-[12px]"
          />
        </div>
        <div className="max-h-60 overflow-y-auto py-1">
          {filteredCurrencies.map((currency) => (
            <button
              key={currency.code}
              type="button"
              onClick={() => {
                onChange(currency.code);
                setOpen(false);
                setQuery("");
              }}
              className={cn(
                "flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[12px] hover:bg-muted/60",
                value === currency.code && "bg-muted/40 font-semibold",
              )}
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="w-7 shrink-0 font-semibold tabular-nums text-foreground">
                  {currency.code}
                </span>
                <span className="truncate text-muted-foreground">
                  {currency.label}
                </span>
              </span>
              <span className="shrink-0 text-muted-foreground">
                {currency.symbol}
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function LegalEntityCard({
  name,
  businessCurrency,
  businessCurrencyLabel,
  sourceCurrencies = [],
  reportsInto = [],
  inPerimeter = true,
  highlighted = true,
  highlights = [],
  dealEvents = EMPTY_DEAL_EVENTS,
  analysisInterval,
  activitySummary,
  consolidatedGroup = false,
  fileCoverage,
  userAddedMessage,
  draftAttachments = [],
  attachmentActionsDisabled = false,
  editable = false,
  perimeterEditable = false,
  parentEntityId,
  relationshipType,
  parentOptions = [],
  onNameChange,
  onBusinessCurrencyChange,
  onRelationshipChange,
  onTogglePerimeter,
  onActivitySummaryChange,
  onDealEventsChange,
  onRemove,
  onAttachFiles,
  onDetachAttachment,
  onDownloadAttachment,
}: LegalEntityCardProps) {
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [perimeterHovered, setPerimeterHovered] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  const saveName = () => {
    const nextName = nameDraft.trim();
    if (nextName && onNameChange) onNameChange(nextName);
    setEditingName(false);
  };
  const opacity = !highlighted
    ? "opacity-25"
    : !inPerimeter
      ? "opacity-60"
      : "";
  const orderedDealEvents = [...dealEvents].sort((left, right) =>
    left.type === right.type ? 0 : left.type === "acquisition" ? -1 : 1);
  const updateDealEvent = (
    type: BuilderLegalEntityDealEvent["type"],
    effectiveMonth: string | null,
  ) => {
    onDealEventsChange?.(
      dealEvents.map((event) =>
        event.type === type
          ? { ...event, effective_month: effectiveMonth }
          : event),
    );
  };
  const removeDealEvent = (type: BuilderLegalEntityDealEvent["type"]) => {
    onDealEventsChange?.(dealEvents.filter((event) => event.type !== type));
  };
  const addDealEvent = (type: BuilderLegalEntityDealEvent["type"]) => {
    onDealEventsChange?.([
      ...dealEvents,
      { type, effective_month: null },
    ]);
  };

  return (
    <article
      className={`relative w-[236px] rounded-xl border bg-card p-3 transition-opacity ${
        inPerimeter ? "border-border" : "border-dashed border-border"
      } ${opacity}`}
    >
      {editable && onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${name}`}
          title="Remove from the entity chart"
          className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-foreground text-background hover:bg-foreground/80"
        >
          <X aria-hidden="true" size={10} />
        </button>
      )}

      <div className="flex items-start justify-between gap-1.5">
        {editingName && onNameChange ? (
          <input
            autoFocus
            aria-label="Legal entity name"
            value={nameDraft}
            onChange={(event) => setNameDraft(event.target.value)}
            onBlur={saveName}
            onKeyDown={(event) => {
              if (event.key === "Enter") saveName();
              if (event.key === "Escape") setEditingName(false);
            }}
            className="min-w-0 flex-1 rounded bg-muted px-1 py-0.5 text-[11.5px] font-semibold outline-none"
          />
        ) : editable && onNameChange ? (
          <button
            type="button"
            onClick={() => {
              setNameDraft(name);
              setEditingName(true);
            }}
            title="Edit legal entity name"
            className="min-w-0 flex-1 cursor-text text-left text-[11.5px] font-semibold leading-[1.3]"
          >
            {name}
          </button>
        ) : (
          <div className="min-w-0 flex-1 text-[11.5px] font-semibold leading-[1.3]">
            {name}
          </div>
        )}

        <div className="flex shrink-0">
          <BusinessCurrencyPill
            value={businessCurrency}
            label={businessCurrencyLabel}
            sourceCurrencies={sourceCurrencies}
            onChange={editable ? onBusinessCurrencyChange : undefined}
          />
        </div>
      </div>

      {reportsInto.length > 0 && !editable && (
        <p className="mt-1 text-[8.5px] text-muted-foreground/70">
          Reports into: {reportsInto.join(", ")}
        </p>
      )}

      {editable && onRelationshipChange && (
        <div className="mt-1 grid grid-cols-2 gap-1">
          <label className="grid gap-0.5 text-[7.5px] text-muted-foreground/70">
            Parent
            <select
              value={parentEntityId ?? ""}
              onChange={(event) => {
                const nextParentId = event.target.value || null;
                onRelationshipChange(
                  nextParentId,
                  nextParentId ? relationshipType ?? "subsidiary" : null,
                );
              }}
              className="min-w-0 rounded bg-muted px-1 py-0.5 text-[8.5px] text-foreground outline-none"
            >
              <option value="">No parent</option>
              {parentOptions.map((option) => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </select>
          </label>
          {parentEntityId && (
            <label className="grid gap-0.5 text-[7.5px] text-muted-foreground/70">
              Relationship
              <select
                value={relationshipType ?? "subsidiary"}
                onChange={(event) =>
                  onRelationshipChange(
                    parentEntityId,
                    event.target.value as BuilderLegalRelationshipType,
                  )}
                className="min-w-0 rounded bg-muted px-1 py-0.5 text-[8.5px] text-foreground outline-none"
              >
                {RELATIONSHIP_TYPES.map((relationship) => (
                  <option key={relationship} value={relationship}>
                    {relationship}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}

      {onTogglePerimeter && (
        <button
          type="button"
          disabled={!perimeterEditable}
          onClick={onTogglePerimeter}
          onMouseEnter={() => setPerimeterHovered(true)}
          onMouseLeave={() => setPerimeterHovered(false)}
          title={perimeterEditable
            ? inPerimeter
              ? "One click takes it out of the deal"
              : "One click brings it back into the deal"
            : undefined}
          className={`mt-1 rounded-full px-2 py-0.5 text-[8.5px] font-medium disabled:cursor-default ${
            inPerimeter
              ? "bg-foreground text-background hover:bg-foreground/80"
              : "border border-border text-muted-foreground hover:border-foreground hover:text-foreground"
          }`}
        >
          {inPerimeter
            ? perimeterHovered && perimeterEditable
              ? "Take out of the deal"
              : "In the transaction perimeter"
            : perimeterHovered && perimeterEditable
              ? "Bring back into the deal"
              : "Out of the perimeter"}
        </button>
      )}

      {consolidatedGroup && (
        <div className="mt-1.5">
          <span className="rounded-full border border-foreground px-1.5 py-0.5 text-[8.5px] font-semibold text-foreground">
            Consolidated group
          </span>
        </div>
      )}

      {highlights.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {highlights.map((highlight) => (
            <span
              key={highlight}
              className="rounded-full border border-foreground px-1.5 py-0.5 text-[8.5px] font-semibold text-foreground"
            >
              {highlight}
            </span>
          ))}
        </div>
      )}

      {(orderedDealEvents.length > 0 || (editable && onDealEventsChange)) && (
        <div className="mt-1.5 space-y-1">
          {orderedDealEvents.map((dealEvent) => {
            const presentation = dealEventPresentation(
              dealEvent,
              analysisInterval,
            );
            return (
              <div
                key={dealEvent.type}
                className="flex min-w-0 items-center gap-1.5"
              >
                <span
                  className={cn(
                    "shrink-0 rounded-full border px-1.5 py-0.5 text-[8.5px] font-semibold",
                    isMidPeriodDealEvent(dealEvent, analysisInterval)
                      ? "border-orange-200 bg-orange-100 text-orange-700"
                      : "border-foreground text-foreground",
                  )}
                >
                  {presentation.label}
                </span>
                {editable && onDealEventsChange ? (
                  <>
                    <input
                      type="month"
                      aria-label={`${presentation.label} effective month`}
                      value={dealEvent.effective_month ?? ""}
                      onChange={(event) =>
                        updateDealEvent(
                          dealEvent.type,
                          event.target.value || null,
                        )}
                      className="min-w-0 flex-1 rounded bg-muted px-1 py-0.5 text-[8.5px] outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => removeDealEvent(dealEvent.type)}
                      aria-label={`Remove ${presentation.label}`}
                      title={`Remove ${presentation.label}`}
                      className="shrink-0 text-muted-foreground/70 hover:text-foreground"
                    >
                      <X aria-hidden="true" size={9} />
                    </button>
                  </>
                ) : (
                  <span className="truncate text-[8.5px] text-muted-foreground">
                    {presentation.detail}
                  </span>
                )}
              </div>
            );
          })}
          {editable && onDealEventsChange && dealEvents.length < 2 && (
            <div className="flex flex-wrap gap-1">
              {(["acquisition", "disposal"] as const)
                .filter(
                  (type) => !dealEvents.some((event) => event.type === type),
                )
                .map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => addDealEvent(type)}
                    className="text-[8.5px] text-muted-foreground underline underline-offset-2 hover:text-foreground"
                  >
                    Add {type}
                  </button>
                ))}
            </div>
          )}
        </div>
      )}

      <LegalEntityActivitySummary
        value={activitySummary}
        onChange={editable ? onActivitySummaryChange : undefined}
      />

      {onAttachFiles && (
        <div className="mt-2 border-t border-border/60 pt-2">
          <input
            ref={attachmentInputRef}
            type="file"
            multiple
            disabled={attachmentActionsDisabled}
            accept={PROJECT_DOCUMENT_ACCEPT}
            className="hidden"
            aria-label={`Attach files to ${name}`}
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = "";
              if (files.length > 0) onAttachFiles(files);
            }}
          />
          <button
            type="button"
            disabled={attachmentActionsDisabled}
            onClick={() => attachmentInputRef.current?.click()}
            className="flex items-center gap-1 text-[9px] font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            <Paperclip aria-hidden="true" size={10} /> Attach source files
          </button>
          {draftAttachments.length > 0 && (
            <ul className="mt-1.5 space-y-1">
              {draftAttachments.map((attachment) => (
                <li
                  key={attachment.key}
                  className="flex min-w-0 items-center gap-1 rounded bg-muted/60 px-1.5 py-1"
                >
                  {attachment.status === "uploading" ? (
                    <Loader2
                      aria-label="Uploading"
                      className="h-3 w-3 shrink-0 animate-spin"
                    />
                  ) : (
                    <Paperclip aria-hidden="true" className="h-3 w-3 shrink-0" />
                  )}
                  <span
                    className={`min-w-0 flex-1 truncate text-[8.5px] ${
                      attachment.status === "error" ? "text-destructive" : ""
                    }`}
                    title={attachment.filename}
                  >
                    {attachment.filename}
                  </span>
                  {attachment.status === "error" && (
                    <span className="shrink-0 text-[8px] text-destructive">
                      Upload failed
                    </span>
                  )}
                  {attachment.status === "ready" && onDownloadAttachment && (
                    <button
                      type="button"
                      aria-label={`Download ${attachment.filename}`}
                      onClick={() => onDownloadAttachment(attachment)}
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-background"
                    >
                      <Download aria-hidden="true" size={10} />
                    </button>
                  )}
                  {onDetachAttachment && (
                    <button
                      type="button"
                      disabled={attachmentActionsDisabled}
                      aria-label={`Detach ${attachment.filename}`}
                      onClick={() => onDetachAttachment(attachment.key)}
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-background disabled:opacity-50"
                    >
                      <X aria-hidden="true" size={10} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {userAddedMessage ? (
        <p className="mt-1.5 text-[9px] text-muted-foreground">
          {userAddedMessage}
        </p>
      ) : (
        <LegalEntityFileCoverage {...fileCoverage} />
      )}
    </article>
  );
}
